// Reviewer surface for grading capstone essays -- see ADR-0005.
//
// Access is enforced by the backend (requireReviewer); everything here is
// presentation. A non-reviewer who navigates to /review directly gets the
// same "not a reviewer" state, because every call below 403s for them.

import { useEffect, useState, useCallback } from 'react';
import { useAuth, SignInButton } from '@clerk/react';
import theme from '../../theme';
import PageHeader from '../../components/PageHeader';
import {
  fetchReviewQueue,
  fetchSubmissionForReview,
  recordReviewDecision,
  fetchWaivers,
  grantWaiver,
  revokeWaiver,
} from '../../lib/capstoneApi';

const RUBRIC_CRITERIA = [
  { key: 'argument', label: 'Argument' },
  { key: 'sources', label: 'Sources' },
  { key: 'clarity', label: 'Clarity' },
  { key: 'counterarguments', label: 'Counterarguments' },
];
const RUBRIC_MAX = 5;

const FILTERS = [
  { key: 'submitted', label: 'Awaiting review' },
  { key: 'needs_revision', label: 'Returned' },
  { key: 'passed', label: 'Passed' },
  { key: 'all', label: 'All' },
];

const CSS = `
  .waiver-panel {
    margin-top: 40px;
    padding: 16px;
    background: ${theme.bgCard};
    border-radius: 4px;
  }
  .waiver-panel summary {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px;
    color: ${theme.gold};
    cursor: pointer;
  }
  .waiver-note {
    font-size: 13px;
    color: ${theme.textMuted};
    margin: 12px 0;
  }
  .waiver-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 8px 0;
    border-top: 1px solid rgba(var(--ap-gold-rgb), 0.15);
  }
  .waiver-row code { font-size: 12px; color: ${theme.textPrimary}; }
  .waiver-reason { font-size: 13px; color: ${theme.textMuted}; flex: 1; }
  .waiver-form {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 16px;
  }
  .waiver-form input {
    flex: 1;
    min-width: 200px;
    padding: 8px 10px;
    background: ${theme.bgDark};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.25);
    border-radius: 3px;
    color: ${theme.textPrimary};
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px;
  }

  .rev-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 24px;
  }
  .rev-filter {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 7px 14px;
    border-radius: 3px;
    border: 1px solid rgba(var(--ap-gold-rgb), 0.3);
    background: transparent;
    color: ${theme.textMuted};
    cursor: pointer;
  }
  .rev-filter:hover { border-color: rgba(var(--ap-gold-rgb), 0.6); }
  .rev-filter.active {
    background: rgba(var(--ap-gold-rgb), 0.12);
    border-color: ${theme.gold};
    color: ${theme.gold};
  }
  .rev-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    width: 100%;
    text-align: left;
    padding: 16px 20px;
    margin-bottom: 10px;
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.22);
    border-radius: 4px;
    cursor: pointer;
  }
  .rev-row:hover { border-color: rgba(var(--ap-gold-rgb), 0.5); }
  .rev-row h3 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 19px;
    color: ${theme.textPrimary};
    margin: 0 0 4px;
  }
  .rev-row .meta {
    font-size: 13px;
    color: ${theme.textMuted};
  }
  .rev-badge {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 2px;
    white-space: nowrap;
  }
  .rev-badge.submitted { background: rgba(var(--ap-gold-rgb), 0.15); color: ${theme.gold}; }
  .rev-badge.passed { background: rgba(var(--ap-success-rgb), 0.15); color: var(--ap-success); }
  .rev-badge.needs_revision { background: rgba(var(--ap-error-rgb), 0.15); color: var(--ap-error); }

  .rev-detail {
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.25);
    border-radius: 4px;
    padding: 28px 32px;
  }
  .rev-back {
    background: none;
    border: none;
    color: ${theme.gold};
    cursor: pointer;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px;
    letter-spacing: 0.04em;
    padding: 0;
    margin-bottom: 20px;
  }
  .rev-essay {
    white-space: pre-wrap;
    line-height: 1.75;
    color: ${theme.textPrimary};
    max-height: 460px;
    overflow-y: auto;
    padding: 20px;
    margin: 20px 0;
    border: 1px solid rgba(var(--ap-text-muted-rgb), 0.2);
    border-radius: 3px;
  }
  .rev-sources { color: ${theme.textMuted}; font-size: 14px; margin: 0 0 20px; padding-left: 20px; }
  .rev-sources li { margin-bottom: 6px; }
  .rev-rubric {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 14px;
    margin: 20px 0;
  }
  .rev-rubric label {
    display: block;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${theme.textMuted};
    margin-bottom: 6px;
  }
  .rev-rubric input, .rev-notes {
    width: 100%;
    padding: 9px 12px;
    background: ${theme.bgDark};
    border: 1px solid rgba(var(--ap-text-muted-rgb), 0.3);
    border-radius: 3px;
    color: ${theme.textPrimary};
    font-family: inherit;
    font-size: 15px;
  }
  .rev-notes { min-height: 130px; line-height: 1.6; resize: vertical; }
  .rev-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 20px; }
  .rev-err { color: var(--ap-error); font-size: 14px; margin-top: 14px; }
  .rev-ok { color: var(--ap-success); font-size: 14px; margin-top: 14px; }
  .rev-empty { color: ${theme.textMuted}; }
`;

function formatDate(value) {
  if (!value) return '--';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function StatusBadge({ status }) {
  return <span className={`rev-badge ${status}`}>{status.replace('_', ' ')}</span>;
}

function ReviewDetail({ getToken, id, onBack, onGraded }) {
  const [submission, setSubmission] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    let active = true;
    fetchSubmissionForReview(getToken, id)
      .then((doc) => {
        if (!active) return;
        setSubmission(doc);
        setNotes(doc.reviewerNotes || '');
        setScores(doc.rubricScores || {});
      })
      .catch(() => active && setLoadError(true));
    return () => { active = false; };
  }, [getToken, id]);

  async function decide(decision) {
    setSaving(true);
    setError('');
    setSaved('');
    try {
      const updated = await recordReviewDecision(getToken, id, {
        decision,
        reviewerNotes: notes,
        rubricScores: scores,
      });
      setSubmission(updated);
      // Whether the student was actually emailed is worth stating plainly:
      // the decision saves either way, so silence here would leave you
      // assuming they were told when they may not have been (ADR-0006).
      const outcome = decision === 'passed' ? 'Recorded as passed.' : 'Returned for revision.';
      setSaved(
        updated.notified
          ? `${outcome} The student has been emailed.`
          : `${outcome} The student was NOT emailed - they'll see it next time they open their Capstone page.`
      );
      onGraded();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loadError) return <p className="rev-err">Couldn't load this submission.</p>;
  if (!submission) return <p className="rev-empty">Loading...</p>;

  return (
    <div className="rev-detail">
      <button type="button" className="rev-back" onClick={onBack}>&larr; Back to queue</button>

      <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: theme.textPrimary, margin: '0 0 6px' }}>
        {submission.title}
      </h2>
      <p style={{ color: theme.textMuted, fontSize: '14px', margin: '0 0 4px' }}>
        {submission.wordCount} words &middot; submitted {formatDate(submission.submittedAt)} &middot;{' '}
        <StatusBadge status={submission.status} />
      </p>
      {/* Identified by Clerk user ID only -- no name or email is fetched, which
          keeps grading close to blind and avoids pulling PII we don't need. */}
      <p style={{ color: theme.textMuted, fontSize: '12px', margin: '0 0 18px' }}>
        Student: <code>{submission.clerkUserId}</code>
      </p>

      <p style={{ color: theme.textPrimary, fontStyle: 'italic', margin: '0 0 18px' }}>
        {submission.questionStatement}
      </p>

      <div className="rev-essay">{submission.essayText}</div>

      <h3 style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '13px', letterSpacing: '0.05em', textTransform: 'uppercase', color: theme.textMuted }}>
        Sources cited ({submission.sourcesCited?.length || 0})
      </h3>
      <ul className="rev-sources">
        {(submission.sourcesCited || []).map((s, i) => <li key={i}>{s}</li>)}
      </ul>

      <h3 style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '13px', letterSpacing: '0.05em', textTransform: 'uppercase', color: theme.textMuted }}>
        Rubric (0&ndash;{RUBRIC_MAX}, optional)
      </h3>
      <div className="rev-rubric">
        {RUBRIC_CRITERIA.map(({ key, label }) => (
          <div key={key}>
            <label htmlFor={`rubric-${key}`}>{label}</label>
            <input
              id={`rubric-${key}`}
              type="number"
              min="0"
              max={RUBRIC_MAX}
              step="0.5"
              value={scores[key] ?? ''}
              onChange={(e) => setScores((prev) => ({ ...prev, [key]: e.target.value }))}
            />
          </div>
        ))}
      </div>

      <label
        htmlFor="reviewer-notes"
        style={{ display: 'block', fontFamily: "'Josefin Sans', sans-serif", fontSize: '13px', letterSpacing: '0.05em', textTransform: 'uppercase', color: theme.textMuted, margin: '0 0 8px' }}
      >
        Notes to the student {/* required only when returning -- the backend enforces this too */}
      </label>
      <textarea
        id="reviewer-notes"
        className="rev-notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="What worked, what needs to change, and why."
      />

      <div className="rev-actions">
        <button type="button" className="btn-primary" disabled={saving} onClick={() => decide('passed')}>
          {saving ? 'Saving...' : 'Pass'}
        </button>
        <button type="button" className="btn-secondary" disabled={saving} onClick={() => decide('needs_revision')}>
          {saving ? 'Saving...' : 'Return for revision'}
        </button>
      </div>

      {error && <p className="rev-err">{error}</p>}
      {saved && <p className="rev-ok">{saved}</p>}
    </div>
  );
}

function ReviewQueue({ getToken, filter, onFilterChange, onOpen, reloadKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null);
    setError(false);
    fetchReviewQueue(getToken, { status: filter })
      .then((res) => active && setData(res))
      .catch(() => active && setError(true));
    return () => { active = false; };
  }, [getToken, filter, reloadKey]);

  return (
    <>
      <div className="rev-filters">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`rev-filter ${filter === key ? 'active' : ''}`}
            onClick={() => onFilterChange(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="rev-err">Couldn't load the review queue.</p>}
      {!error && !data && <p className="rev-empty">Loading...</p>}
      {data && data.submissions.length === 0 && (
        <p className="rev-empty">Nothing here.</p>
      )}
      {data && data.submissions.map((s) => (
        <button key={s._id} type="button" className="rev-row" onClick={() => onOpen(s._id)}>
          <span>
            <h3>{s.title}</h3>
            <span className="meta">
              {s.wordCount} words &middot; submitted {formatDate(s.submittedAt)}
            </span>
          </span>
          <StatusBadge status={s.status} />
        </button>
      ))}
      {data && data.total > data.submissions.length && (
        <p className="rev-empty" style={{ fontSize: '13px' }}>
          Showing {data.submissions.length} of {data.total}.
        </p>
      )}
    </>
  );
}

// Granting a waiver only through mongosh or curl would repeat exactly the dead
// end ADR-0005 was written to fix: a decision the product depends on that has
// no way to be made. Kept deliberately plain -- it is a rare, deliberate act,
// not a workflow.
function WaiverPanel({ getToken }) {
  const [waivers, setWaivers] = useState(null);
  const [clerkUserId, setClerkUserId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    fetchWaivers(getToken).then((r) => setWaivers(r.waivers || [])).catch(() => setWaivers([]));
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    setBusy(true); setError(null); setMessage(null);
    try {
      await grantWaiver(getToken, { clerkUserId: clerkUserId.trim(), reason: reason.trim() });
      setMessage('Waiver granted.');
      setClerkUserId(''); setReason('');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    setBusy(true); setError(null); setMessage(null);
    try {
      await revokeWaiver(getToken, id);
      setMessage('Waiver revoked.');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="waiver-panel">
      <summary>Coursework waivers{waivers?.length ? ` (${waivers.length})` : ''}</summary>
      <p className="waiver-note">
        Lets one named student sit the exam without meeting the coursework threshold. Use it
        when a retired or reseeded lesson has closed an exam someone had open. The reason is
        recorded against your name.
      </p>

      {waivers === null && <p className="waiver-note">Loading...</p>}
      {waivers?.length === 0 && <p className="waiver-note">None granted.</p>}
      {waivers?.map((w) => (
        <div key={w.clerkUserId} className="waiver-row">
          <code>{w.clerkUserId}</code>
          <span className="waiver-reason">{w.reason}</span>
          <button type="button" className="btn-secondary" onClick={() => revoke(w.clerkUserId)} disabled={busy}>
            Revoke
          </button>
        </div>
      ))}

      <div className="waiver-form">
        <input
          type="text"
          value={clerkUserId}
          onChange={(e) => setClerkUserId(e.target.value)}
          placeholder="Clerk user id (user_...)"
        />
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why this student is being waived"
        />
        <button
          type="button"
          className="btn-primary"
          onClick={grant}
          disabled={busy || !clerkUserId.trim() || !reason.trim()}
        >
          {busy ? 'Saving...' : 'Grant waiver'}
        </button>
      </div>
      {message && <span className="save-msg">{message}</span>}
      {error && <span className="err-msg">{error}</span>}
    </details>
  );
}

function ReviewPage() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [filter, setFilter] = useState('submitted');
  const [openId, setOpenId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const handleGraded = useCallback(() => setReloadKey((k) => k + 1), []);

  return (
    <div className="page-container">
      <style>{CSS}</style>
      <PageHeader
        title="Review"
        description="Capstone essays awaiting a decision. Passing here is one of the two gates the Diploma is awarded on."
      />

      {!isLoaded && <p style={{ color: theme.textMuted }}>Loading...</p>}

      {isLoaded && !isSignedIn && (
        <SignInButton mode="modal">
          <button type="button" className="btn-primary">Sign in</button>
        </SignInButton>
      )}

      {isLoaded && isSignedIn && (
        openId
          ? <ReviewDetail
              getToken={getToken}
              id={openId}
              onBack={() => setOpenId(null)}
              onGraded={handleGraded}
            />
          : <>
              <ReviewQueue
                getToken={getToken}
                filter={filter}
                onFilterChange={setFilter}
                onOpen={setOpenId}
                reloadKey={reloadKey}
              />
              <WaiverPanel getToken={getToken} />
            </>
      )}
    </div>
  );
}

export default ReviewPage;
